import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Alert, LinearProgress,
  Stack, Chip, Grid, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Switch, FormControlLabel, Divider,
} from '@mui/material';
import {
  Inventory as ProductsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { platformApi } from '../api/services';

interface Product {
  id: string;
  name: string;
  description: string | null;
  pricePerSeatPence: number;
  currency: string;
  billingInterval: string;
  maxExecutiveSeats: number;
  isPopular: boolean;
  isCustomPricing: boolean;
  stripeProductId?: string;
  stripePriceId?: string;
  aiPlanningEnabled: boolean;
  transcriptProcessingEnabled: boolean;
  imageAnalysisEnabled: boolean;
  pdfExportEnabled: boolean;
  webhooksEnabled: boolean;
  apiAccessEnabled: boolean;
  ssoEnabled: boolean;
  customBrandingEnabled: boolean;
  crossExecutiveReportingEnabled: boolean;
  prioritySupportEnabled: boolean;
  dedicatedOnboardingEnabled: boolean;
}

const COLORS = {
  primary: '#6C9BFF',
  secondary: '#FF8A65',
  success: '#69F0AE',
  error: '#FF5252',
  warning: '#FFB74D',
  bgPaper: '#121829',
  textPrimary: '#E8ECF4',
  textSecondary: '#8892A8',
  gridLine: '#1E293B',
};

const FEATURE_FLAGS: { key: keyof Product; label: string }[] = [
  { key: 'aiPlanningEnabled', label: 'AI Planning' },
  { key: 'transcriptProcessingEnabled', label: 'Transcript Processing' },
  { key: 'imageAnalysisEnabled', label: 'Image Analysis' },
  { key: 'pdfExportEnabled', label: 'PDF Export' },
  { key: 'webhooksEnabled', label: 'Webhooks' },
  { key: 'apiAccessEnabled', label: 'API Access' },
  { key: 'ssoEnabled', label: 'SSO' },
  { key: 'customBrandingEnabled', label: 'Custom Branding' },
  { key: 'crossExecutiveReportingEnabled', label: 'Cross-Executive Reporting' },
  { key: 'prioritySupportEnabled', label: 'Priority Support' },
  { key: 'dedicatedOnboardingEnabled', label: 'Dedicated Onboarding' },
];

const emptyProduct: Omit<Product, 'id'> = {
  name: '',
  description: '',
  pricePerSeatPence: 0,
  currency: 'GBP',
  billingInterval: 'Monthly',
  maxExecutiveSeats: 1,
  isPopular: false,
  isCustomPricing: false,
  stripeProductId: '',
  stripePriceId: '',
  aiPlanningEnabled: false,
  transcriptProcessingEnabled: false,
  imageAnalysisEnabled: false,
  pdfExportEnabled: false,
  webhooksEnabled: false,
  apiAccessEnabled: false,
  ssoEnabled: false,
  customBrandingEnabled: false,
  crossExecutiveReportingEnabled: false,
  prioritySupportEnabled: false,
  dedicatedOnboardingEnabled: false,
};

function formatPrice(pence: number, currency: string): string {
  const amount = pence / 100;
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

export default function PlatformProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingProduct, setEditingProduct] = useState<Partial<Product> | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<{ id: string; type: 'success' | 'error'; text: string } | null>(null);

  // Stripe settings state
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [stripeLiveMode, setStripeLiveMode] = useState(false);
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [stripePublishableKey, setStripePublishableKey] = useState('');
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState('');
  const [stripeSaveLoading, setStripeSaveLoading] = useState(false);
  const [stripeSaveMessage, setStripeSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await platformApi.getProducts();
      setProducts(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStripeSettings = useCallback(async () => {
    try {
      const data = await platformApi.getStripeSettings();
      setStripeConfigured(data.isConfigured ?? false);
      setStripeLiveMode(data.isLiveMode ?? false);
      if (data.publishableKey) setStripePublishableKey(data.publishableKey);
    } catch {
      // Stripe settings not loaded — non-critical
    }
  }, []);

  useEffect(() => { loadProducts(); loadStripeSettings(); }, [loadProducts, loadStripeSettings]);

  const handleSaveStripeSettings = async () => {
    setStripeSaveLoading(true);
    setStripeSaveMessage(null);
    try {
      const payload: Record<string, string> = {};
      if (stripeSecretKey) payload.secretKey = stripeSecretKey;
      if (stripePublishableKey) payload.publishableKey = stripePublishableKey;
      if (stripeWebhookSecret) payload.webhookSecret = stripeWebhookSecret;
      const result = await platformApi.updateStripeSettings(payload);
      setStripeConfigured(result.isConfigured ?? false);
      setStripeLiveMode(result.isLiveMode ?? false);
      setStripeSaveMessage({ type: 'success', text: result.message || 'Stripe settings saved.' });
      setStripeSecretKey('');
      setStripeWebhookSecret('');
      loadStripeSettings();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save Stripe settings';
      setStripeSaveMessage({ type: 'error', text: msg });
    } finally {
      setStripeSaveLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingProduct({ ...emptyProduct });
    setIsCreating(true);
    setSaveError(null);
  };

  const handleOpenEdit = (product: Product) => {
    setEditingProduct({ ...product });
    setIsCreating(false);
    setSaveError(null);
  };

  const handleClose = () => {
    setEditingProduct(null);
    setIsCreating(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!editingProduct?.name?.trim()) {
      setSaveError('Product name is required');
      return;
    }
    setSaveLoading(true);
    setSaveError(null);
    try {
      if (isCreating) {
        await platformApi.createProduct(editingProduct);
      } else if (editingProduct.id) {
        await platformApi.updateProduct(editingProduct.id, editingProduct);
      }
      handleClose();
      loadProducts();
    } catch (err: any) {
      setSaveError(err.response?.data?.message || err.message || 'Failed to save product');
    } finally {
      setSaveLoading(false);
    }
  };

  const updateField = (field: string, value: any) => {
    setEditingProduct(prev => prev ? { ...prev, [field]: value } : prev);
  };

  const handleSyncToStripe = async (productId: string) => {
    setSyncingId(productId);
    setSyncMessage(null);
    try {
      const result = await platformApi.syncProductToStripe(productId);
      setSyncMessage({ id: productId, type: 'success', text: result.message || 'Synced to Stripe successfully.' });
      loadProducts();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to sync to Stripe';
      setSyncMessage({ id: productId, type: 'error', text: msg });
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <Box>
      {/* Stripe Configuration */}
      <Card sx={{ p: 3, mb: 3, bgcolor: '#121829' }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>Stripe Configuration</Typography>

        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Chip label={stripeConfigured ? 'Connected' : 'Not Connected'}
                color={stripeConfigured ? 'success' : 'error'} size="small" />
          {stripeLiveMode && <Chip label="Live Mode" color="warning" size="small" />}
          {stripeConfigured && !stripeLiveMode && <Chip label="Test Mode" size="small" />}
        </Box>

        {stripeSaveMessage && (
          <Alert severity={stripeSaveMessage.type} sx={{ mb: 2 }} onClose={() => setStripeSaveMessage(null)}>
            {stripeSaveMessage.text}
          </Alert>
        )}

        <TextField
          label="Secret Key"
          type="password"
          fullWidth
          value={stripeSecretKey}
          onChange={e => setStripeSecretKey(e.target.value)}
          sx={{ mb: 2 }}
          helperText="sk_test_... or sk_live_..."
          placeholder={stripeConfigured ? '(unchanged — enter new value to update)' : ''}
        />
        <TextField
          label="Publishable Key"
          fullWidth
          value={stripePublishableKey}
          onChange={e => setStripePublishableKey(e.target.value)}
          sx={{ mb: 2 }}
          helperText="pk_test_... or pk_live_..."
        />
        <TextField
          label="Webhook Secret"
          type="password"
          fullWidth
          value={stripeWebhookSecret}
          onChange={e => setStripeWebhookSecret(e.target.value)}
          sx={{ mb: 2 }}
          helperText="whsec_..."
          placeholder={stripeConfigured ? '(unchanged — enter new value to update)' : ''}
        />
        <Button
          variant="contained"
          onClick={handleSaveStripeSettings}
          disabled={stripeSaveLoading}
        >
          {stripeSaveLoading ? 'Saving...' : 'Save Stripe Settings'}
        </Button>
      </Card>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ProductsIcon sx={{ color: 'error.main', fontSize: 28 }} />
          <Box>
            <Typography variant="h4">Products & Pricing</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage billing products and feature flags
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          color="error"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{ fontWeight: 600 }}
        >
          Create Product
        </Button>
      </Box>

      {loading && <LinearProgress color="error" sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Product Cards */}
      <Grid container spacing={2.5}>
        {products.map(product => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={product.id}>
            <Card sx={{ bgcolor: COLORS.bgPaper, height: '100%', position: 'relative' }}>
              {product.isPopular && (
                <Chip
                  label="Popular"
                  size="small"
                  color="warning"
                  sx={{ position: 'absolute', top: 12, right: 12, fontWeight: 600 }}
                />
              )}
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600 }}>
                      {product.name}
                    </Typography>
                    {product.description && (
                      <Typography variant="body2" sx={{ color: COLORS.textSecondary, mt: 0.5 }}>
                        {product.description}
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    {product.isCustomPricing ? (
                      <Typography variant="h5" sx={{ color: COLORS.warning, fontWeight: 700 }}>
                        Custom Pricing
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography variant="h5" sx={{ color: COLORS.primary, fontWeight: 700 }}>
                          {formatPrice(product.pricePerSeatPence, product.currency)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                          / seat / {product.billingInterval.toLowerCase()}
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
                      Up to {product.maxExecutiveSeats} executive seat{product.maxExecutiveSeats !== 1 ? 's' : ''}
                    </Typography>
                  </Box>

                  <Divider />

                  {/* Feature flags */}
                  <Box>
                    <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', mb: 1 }}>
                      Features
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {FEATURE_FLAGS.map(flag => {
                        const enabled = Boolean(product[flag.key]);
                        return (
                          <Chip
                            key={flag.key}
                            label={flag.label}
                            size="small"
                            sx={{
                              bgcolor: enabled ? 'rgba(105, 240, 174, 0.12)' : 'rgba(255,255,255,0.04)',
                              color: enabled ? COLORS.success : COLORS.textSecondary,
                              fontWeight: enabled ? 600 : 400,
                              fontSize: '0.7rem',
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </Box>

                  {/* Stripe Integration Status */}
                  <Box>
                    <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                      Stripe
                    </Typography>
                    {product.stripeProductId ? (
                      <Stack spacing={0.25}>
                        <Typography variant="caption" sx={{ color: COLORS.success, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                          Product: {product.stripeProductId}
                        </Typography>
                        {product.stripePriceId && (
                          <Typography variant="caption" sx={{ color: COLORS.success, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                            Price: {product.stripePriceId}
                          </Typography>
                        )}
                      </Stack>
                    ) : (
                      <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
                        Not synced
                      </Typography>
                    )}
                    {syncMessage?.id === product.id && (
                      <Alert severity={syncMessage.type} sx={{ mt: 0.5, py: 0, fontSize: '0.75rem' }} onClose={() => setSyncMessage(null)}>
                        {syncMessage.text}
                      </Alert>
                    )}
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleOpenEdit(product)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="warning"
                      startIcon={<SyncIcon />}
                      onClick={() => handleSyncToStripe(product.id)}
                      disabled={syncingId === product.id}
                    >
                      {syncingId === product.id ? 'Syncing...' : 'Sync to Stripe'}
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {!loading && products.length === 0 && (
          <Grid size={{ xs: 12 }}>
            <Card sx={{ bgcolor: COLORS.bgPaper }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary">No products configured</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Create your first billing product to get started.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Create / Edit Dialog */}
      <Dialog open={editingProduct !== null} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isCreating ? 'Create Product' : 'Edit Product'}</DialogTitle>
        <DialogContent>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          {editingProduct && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Product Name"
                fullWidth
                value={editingProduct.name || ''}
                onChange={e => updateField('name', e.target.value)}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={editingProduct.description || ''}
                onChange={e => updateField('description', e.target.value)}
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Price (pence per seat)"
                  type="number"
                  fullWidth
                  value={editingProduct.pricePerSeatPence || 0}
                  onChange={e => updateField('pricePerSeatPence', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                />
                <TextField
                  label="Currency"
                  select
                  fullWidth
                  value={editingProduct.currency || 'GBP'}
                  onChange={e => updateField('currency', e.target.value)}
                >
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                </TextField>
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Billing Interval"
                  select
                  fullWidth
                  value={editingProduct.billingInterval || 'Monthly'}
                  onChange={e => updateField('billingInterval', e.target.value)}
                >
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="Annual">Annual</MenuItem>
                </TextField>
                <TextField
                  label="Max Executive Seats"
                  type="number"
                  fullWidth
                  value={editingProduct.maxExecutiveSeats || 1}
                  onChange={e => updateField('maxExecutiveSeats', parseInt(e.target.value) || 1)}
                  inputProps={{ min: 1 }}
                />
              </Stack>

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ color: COLORS.textSecondary }}>Stripe Integration</Typography>

              <Stack direction="row" spacing={2}>
                <TextField
                  label="Stripe Product ID"
                  fullWidth
                  value={(editingProduct as any).stripeProductId || ''}
                  onChange={e => updateField('stripeProductId', e.target.value)}
                  placeholder="prod_..."
                />
                <TextField
                  label="Stripe Price ID"
                  fullWidth
                  value={(editingProduct as any).stripePriceId || ''}
                  onChange={e => updateField('stripePriceId', e.target.value)}
                  placeholder="price_..."
                />
              </Stack>

              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={<Switch checked={editingProduct.isPopular || false} onChange={e => updateField('isPopular', e.target.checked)} />}
                  label="Popular"
                />
                <FormControlLabel
                  control={<Switch checked={editingProduct.isCustomPricing || false} onChange={e => updateField('isCustomPricing', e.target.checked)} />}
                  label="Custom Pricing"
                />
              </Stack>

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ color: COLORS.textSecondary }}>Feature Flags</Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                {FEATURE_FLAGS.map(flag => (
                  <FormControlLabel
                    key={flag.key}
                    control={
                      <Switch
                        size="small"
                        checked={Boolean(editingProduct[flag.key])}
                        onChange={e => updateField(flag.key, e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">{flag.label}</Typography>}
                  />
                ))}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleSave} disabled={saveLoading}>
            {saveLoading ? 'Saving...' : isCreating ? 'Create' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
